// Field/operator config for the candidate advanced-filter builder ("Edit
// Filters"). Field keys MUST match the backend FILTER_FIELDS whitelist.

export type FilterFieldType = 'text' | 'enum' | 'array' | 'number' | 'date' | 'bool';
export interface CandidateFilterRow { field: string; op: string; value: string; }

export interface FilterFieldDef {
  key: string; label: string; type: FilterFieldType; options?: Array<{ value: string; label: string }>;
}

const STATUS = ['active', 'inactive', 'placed', 'rejected', 'withdrawn'];
// The current pipeline stage keys (per ats_phase1 — the old
// application/credentialing/onboarding set was remapped). Keep in sync with the
// pipeline_stages defaults so "Stage is …" actually matches records.
const STAGE = [
  'new_lead', 'screening', 'internal_review', 'submitted', 'client_submitted',
  'interview', 'offer', 'confirmed', 'placed', 'not_joined', 'rejected', 'withdrawn',
];
const ROLES = ['RN', 'LPN', 'LVN', 'CNA', 'RT', 'NP', 'PA', 'Other'];
const AVAIL = ['full_time', 'part_time', 'per_diem', 'contract'];
const YESNO = [{ value: 'yes', label: 'Yes' }, { value: 'no', label: 'No' }];
const opt = (vals: string[]) => vals.map((v) => ({ value: v, label: v.replace(/_/g, ' ') }));

export const FILTER_FIELDS: FilterFieldDef[] = [
  // Free-form: type anything and it matches across name, contact, location,
  // specialties, skills, certs, licenses and notes. The "custom filter" box.
  { key: 'keyword', label: 'Keyword (match anywhere)', type: 'text' },
  { key: 'applicant_name', label: 'Applicant Name', type: 'text' },
  { key: 'email', label: 'Email Address', type: 'text' },
  { key: 'phone', label: 'Mobile Number', type: 'text' },
  { key: 'profession', label: 'Profession', type: 'enum', options: opt(ROLES) },
  { key: 'specialty', label: 'Specialty', type: 'array' },
  { key: 'skills', label: 'Skills', type: 'array' },
  { key: 'certifications', label: 'Certifications', type: 'array' },
  { key: 'licenses', label: 'Licenses', type: 'array' },
  { key: 'desired_shift', label: 'Desired Shift', type: 'array' },
  { key: 'status', label: 'Applicant Status', type: 'enum', options: opt(STATUS) },
  { key: 'stage', label: 'Stage', type: 'enum', options: opt(STAGE) },
  { key: 'availability', label: 'Availability', type: 'enum', options: opt(AVAIL) },
  // Yes/No presence + outreach filters.
  { key: 'has_email', label: 'Has Email', type: 'bool', options: YESNO },
  { key: 'has_phone', label: 'Has Phone Number', type: 'bool', options: YESNO },
  { key: 'has_contact', label: 'Has Email or Phone', type: 'bool', options: YESNO },
  { key: 'contacted', label: 'Reached Out Already', type: 'bool', options: YESNO },
  { key: 'in_recruitment', label: 'Progressing in Recruitment', type: 'bool', options: YESNO },
  { key: 'source', label: 'Source', type: 'text' },
  { key: 'recruiter', label: 'Ownership (Recruiter)', type: 'text' },
  { key: 'city', label: 'City', type: 'text' },
  { key: 'state', label: 'State', type: 'text' },
  { key: 'zip', label: 'Zip Code', type: 'text' },
  { key: 'address', label: 'Address', type: 'text' },
  { key: 'education', label: 'Education', type: 'text' },
  { key: 'linkedin_url', label: 'LinkedIn URL', type: 'text' },
  { key: 'recruiter_notes', label: 'Recruiter Notes', type: 'text' },
  { key: 'hr_notes', label: 'HR Notes', type: 'text' },
  { key: 'years_experience', label: 'Years Experience', type: 'number' },
  { key: 'desired_pay_rate', label: 'Desired Pay Rate', type: 'number' },
  { key: 'offered_pay_rate', label: 'Offered Pay Rate', type: 'number' },
  { key: 'availability_start', label: 'Available Start Date', type: 'date' },
  { key: 'created_on', label: 'Created On', type: 'date' },
  { key: 'updated_on', label: 'Updated On', type: 'date' },
];

export const OPERATORS: Record<FilterFieldType, Array<{ value: string; label: string }>> = {
  text: [
    { value: 'contains', label: 'contains' },
    { value: 'equals', label: 'is exactly' },
    { value: 'starts_with', label: 'starts with' },
    { value: 'not_contains', label: 'does not contain' },
    { value: 'is_empty', label: 'is empty' },
    { value: 'is_not_empty', label: 'is not empty' },
  ],
  enum: [{ value: 'is', label: 'is' }, { value: 'is_not', label: 'is not' }],
  bool: [{ value: 'is', label: 'is' }],
  array: [{ value: 'includes', label: 'includes' }, { value: 'not_includes', label: 'excludes' }],
  number: [
    { value: 'eq', label: '=' }, { value: 'gt', label: '>' }, { value: 'lt', label: '<' },
    { value: 'gte', label: '≥' }, { value: 'lte', label: '≤' },
  ],
  date: [
    { value: 'on', label: 'on' }, { value: 'before', label: 'before' },
    { value: 'after', label: 'after' }, { value: 'last_n_days', label: 'in the last (days)' },
  ],
};

export function fieldDef(key: string): FilterFieldDef | undefined {
  return FILTER_FIELDS.find((f) => f.key === key);
}
export function defaultOp(type: FilterFieldType): string {
  return OPERATORS[type][0].value;
}
// Operators that need no value input.
export function isValuelessOp(op: string): boolean {
  return op === 'is_empty' || op === 'is_not_empty';
}
export function filterChipLabel(row: CandidateFilterRow): string {
  const d = fieldDef(row.field);
  if (!d) return row.field;
  const opLabel = (OPERATORS[d.type].find((o) => o.value === row.op)?.label ?? row.op);
  return isValuelessOp(row.op) ? `${d.label} ${opLabel}` : `${d.label} ${opLabel} "${row.value}"`;
}
